import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, MailCheck, MailWarning, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Landing page hit when a user clicks the verification link emailed to them.
 * Routes:
 *   /verify-email/:token  → consumes token and shows success / expired / error.
 *
 * States:
 *   - verifying : initial spinner while we hit the backend
 *   - success   : email verified (or already verified) → CTA to sign in
 *   - expired   : token expired (HTTP 410) → resend form
 *   - error     : invalid/404 or unexpected error → resend form
 */
const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Resend form
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const verify = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-email/${token}`);
      if (res.data?.success) {
        setVerifiedEmail(res.data.email || '');
        setUserType(res.data.user_type || '');
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (code === 410 || detail === 'expired') {
        setStatus('expired');
      } else {
        setStatus('error');
        setErrorMessage(detail || 'We could not verify this link.');
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token || token.length < 32) {
      setStatus('error');
      setErrorMessage('This verification link is malformed.');
      return;
    }
    verify();
  }, [token, verify]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: resendEmail.trim().toLowerCase() });
      setResendSent(true);
      toast.success('If an account exists with this email, a verification link has been sent.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4" data-testid="verify-email-page">
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${
            status === 'success' || status === 'verifying' ? 'bg-green-100' :
            status === 'expired' ? 'bg-amber-100' :
            'bg-red-100'
          }`}>
            {status === 'verifying' && <Loader2 className="h-8 w-8 text-green-600 animate-spin" />}
            {status === 'success' && <CheckCircle2 className="h-8 w-8 text-green-600" data-testid="verify-success-icon" />}
            {status === 'expired' && <MailWarning className="h-8 w-8 text-amber-600" data-testid="verify-expired-icon" />}
            {status === 'error' && <XCircle className="h-8 w-8 text-red-600" data-testid="verify-error-icon" />}
          </div>
          <CardTitle className="text-2xl">
            {status === 'verifying' && 'Verifying your email…'}
            {status === 'success' && 'Email verified'}
            {status === 'expired' && 'Link expired'}
            {status === 'error' && 'Verification failed'}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {status === 'verifying' && (
            <p className="text-center text-gray-600 text-sm">
              Just a moment while we confirm your account…
            </p>
          )}

          {status === 'success' && (
            <div className="space-y-4 text-center">
              <p className="text-gray-700">
                {verifiedEmail ? (
                  <>
                    <span className="font-medium">{verifiedEmail}</span> is now verified.
                  </>
                ) : (
                  'Your email is now verified.'
                )}{' '}
                Welcome to Daye Farm Ltd!
              </p>
              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => navigate(userType === 'investor' ? '/auth?type=investor' : '/auth')}
                data-testid="verify-continue-btn"
              >
                Sign in to continue <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Link to="/" className="block text-sm text-green-700 hover:text-green-800">
                Back to home
              </Link>
            </div>
          )}

          {(status === 'expired' || status === 'error') && (
            <div className="space-y-4">
              <p className="text-center text-gray-700 text-sm">
                {status === 'expired'
                  ? 'This verification link has expired. Enter your email below and we will send you a fresh one.'
                  : (errorMessage || 'We could not verify this link.') +
                    ' Enter your email and we will resend the verification link.'}
              </p>

              {resendSent ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
                  <MailCheck className="h-5 w-5 text-green-600" />
                  <p className="text-sm text-green-800">
                    If an account exists with this email, a fresh verification link has been sent.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleResend} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                    data-testid="resend-email-input"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={resending}
                    data-testid="resend-submit-btn"
                  >
                    {resending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {resending ? 'Sending…' : 'Resend verification email'}
                  </Button>
                </form>
              )}

              <div className="text-center">
                <Link to="/auth" className="text-sm text-green-700 hover:text-green-800">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
